import { Badge, Button, message, Popover, Spin } from "antd";
import { BellOutlined } from "@ant-design/icons";
import React, { useEffect, useRef } from "react";
import { ancanaApi } from "../../api/ancanaApi";
import styled from "styled-components";
import moment from "moment/moment";


const Notification = styled.div<any>`
  width: 400px;
  padding: 12px;
  display: flex;
  flex-direction: row;
  gap: 12px;
  cursor: pointer;
  border-top: 1px solid rgba(150, 150, 150, 0.25);
  background: ${({ seen }) => seen ? "transparent" : "rgba(138,138,138,0.1)"};

  :hover {
    background-color: rgba(135, 135, 135, 0.2);
  }

  position: relative;

  :after {
    content: "";
    position: absolute;
    width: 5px;
    height: 5px;
    border-radius: 5px;
    background-color: ${({ seen }) => seen ? "transparent" : "#1890ff"};
    box-shadow: 0 1px 1px rgba(124, 124, 124, 0.35);
    right: 8px;
    top: calc(50% - 2px);
  }
`;

const USER_COLORS = [
    '#F5BABA',
    '#FAD09A',
    '#B5D4F4',
    '#CECBF6',
    '#c6e3a0',
    '#9FE1CB',
    '#F5C4B3',
    '#F4C0D1',
    '#C0E0FA',
    '#D0EC90',
    '#DCC0F5',
    '#B8D0E0',
  ];

function NotificationTray() {
  const [trayOpen, setTrayOpen] = React.useState(false);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [notifications, setNotifications] = React.useState<{ title: string, seen: boolean, id: number }[]>([]);
  const [loading, setLoading] = React.useState(false);
  const unreadCountRef = useRef(unreadCount);

  const handleMarkAsSeen = (id: number) => {
    const newNotifications = notifications.map(n => {
      if (n.id === id) {
        return { ...n, seen: true };
      }
      return n;
    });
    setNotifications(newNotifications);
  }

  const handleMarkAllAsSeen = () => {
    const newNotifications = notifications.map(n => ({ ...n, seen: true }));
    setNotifications(newNotifications);
    ancanaApi.get(`my-notifications/mark_all_as_seen/`);
  }

  const getNotificationCount = () => {
    ancanaApi.get(`my-notifications/get_unread_count/`).then(res => {
      if (res.data.count === unreadCountRef.current) return;
      if (res.data.count > unreadCountRef.current) {
        message.info('You have new notifications');
      }
      setUnreadCount(res.data.count);
    })
  }

  useEffect(() => {
    setLoading(true);
    ancanaApi.get(`my-notifications/?page_size=50`).then(res => {
      setNotifications(res.data.results);
      setLoading(false);
    });

    const myCountInterval = setInterval(getNotificationCount, 150000);

    return () => {
      clearInterval(myCountInterval);
    }
  }, []);

  useEffect(() => {
    setUnreadCount(notifications.filter(n => !n.seen).length);
  }, [notifications]);

  useEffect(() => {
    if (trayOpen) {
      setLoading(true);
      ancanaApi.get(`my-notifications/?page_size=50&expand=sent_by`).then(res => {
        setNotifications(res.data.results);
        setLoading(false);
      });
    }
  }, [trayOpen]);

  useEffect(() => {
    unreadCountRef.current = unreadCount;
    const titleHasUnreadCount = document.title.match(/\(\d+\)/);
    if (unreadCount > 0) {
      if (titleHasUnreadCount) {
        document.title = document.title.replace(/\(\d+\)/, `(${unreadCount})`);
      } else {
        document.title = `(${unreadCount}) ${document.title}`;
      }
    }
    if (unreadCount === 0 && titleHasUnreadCount) {
      document.title = document.title.replace(/\(\d+\)/, '');
    }
  }, [unreadCount]);

  return (
    <Popover
      placement="bottom"
      title=""
      trigger="click"
      visible={trayOpen}
      onVisibleChange={setTrayOpen}
      content={(
        <div
          className="fw-500"
          style={{
            width: 400,
            display: 'flex', flexDirection: 'column',
            margin: "-12px -16px",
          }}
        >
          <div className="pd-sm disp-flex h-stack-strict center justify-between" style={{ borderBottom: "1px solid rgba(150, 150, 150, 0.25)" }}>
            <div className="fw-700">Notifications</div>
            <Button type="link" size="small" disabled={unreadCount === 0} onClick={handleMarkAllAsSeen}>Mark all as read</Button>
          </div>
          <div style={{ maxHeight: 420, height: 'calc(100vh - 115px)', overflowY: 'auto' }}>
            {loading ? (
              <Spin style={{ marginLeft: 'calc(50% - 30px)' }} className="mt-lg mb-lg" tip="Loading..." />
            ) : (
              notifications.length > 0 ? (
                notifications.map((n: any) => (
                  <Notification key={n.id} seen={n.seen} onClick={() => handleMarkAsSeen(n.id)}>
                    {!!n.sent_by ? (
                      <div
                        style={{
                          backgroundColor: USER_COLORS[n.sent_by.id % USER_COLORS.length],
                          width: 35, height: 35, aspectRatio: '1/1', borderRadius: 20,
                          marginTop: 4,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {n.sent_by.first_name?.charAt(0)}{n.sent_by.last_name?.charAt(0)}
                      </div>
                    ) : (
                      <div
                        style={{
                          background: 'url("https://avatars.slack-edge.com/2025-04-01/8675939355943_67214911dd58f73f6ac3_512.jpg") center center / cover no-repeat',
                          width: 35, height: 35, aspectRatio: '1/1', borderRadius: 20,
                          marginTop: 4
                        }}
                      />
                    )}

                    <div>
                      <div className="fs-2-5 fw-500">{n.title}</div>
                      <div className="fs-2 fw-300">{moment(n.created_at).fromNow()}</div>
                      {n.link && (
                        <Button
                          className="mt-sm" type="primary" size="small"
                          href={n.link}
                        >
                          Resolve
                        </Button>
                      )}
                    </div>
                  </Notification>
                ))
              ) : (
                "You don't have any notifications"
              )
            )}
          </div>
        </div>
      )}
    >
      <Badge count={unreadCount} overflowCount={99} offset={[-12, 8]}>
        <Button type="text" style={{ marginRight: 8 }}><BellOutlined color="white" /></Button>
      </Badge>
    </Popover>
  )
}

export default NotificationTray;
